import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const RELOAD_FLAG = "xale:chunk-reload-attempted";

const STALE_CHUNK_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /ChunkLoadError/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
];

export function isStaleChunkError(err: unknown): boolean {
  if (!err) return false;
  const message = err instanceof Error ? err.message : String(err);
  return STALE_CHUNK_PATTERNS.some((re) => re.test(message));
}

/**
 * Force a one-shot hard reload to fetch the new `index.html` after a deploy.
 * Guarded by sessionStorage so we never loop — if the reloaded page still
 * fails, the user sees the normal error screen.
 *
 * Returns true when a reload was triggered (caller should suspend rendering),
 * false when we've already used our one attempt this session.
 */
export function reloadIfStaleChunk(err: unknown): boolean {
  if (!isStaleChunkError(err)) return false;
  if (typeof window === "undefined") return false;
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch {
    // sessionStorage disabled (private mode etc.) — still reload, just
    // accept that we can't loop-protect for this session.
  }
  window.location.reload();
  return true;
}

/**
 * Drop-in replacement for `React.lazy` that survives the most common
 * post-deploy failure mode: the user's tab has the old `index.html` in
 * memory, which references chunk hashes that no longer exist on the CDN.
 *
 * Strategy: try the import once, retry once on failure (covers transient
 * network blips), and if the second attempt is also a stale-chunk error,
 * trigger a one-shot reload so the browser pulls the new index.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (firstErr) {
      try {
        return await factory();
      } catch (secondErr) {
        if (reloadIfStaleChunk(secondErr)) {
          // Hold Suspense fallback while the reload happens.
          return new Promise<{ default: T }>(() => {});
        }
        throw secondErr;
      }
    }
  });
}

/**
 * Vite emits `vite:preloadError` on the window when a `<link rel="modulepreload">`
 * fails — we want the same auto-reload behavior in that case. Call once at
 * app boot.
 */
let installed = false;
export function installChunkErrorReloader() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("vite:preloadError", (event) => {
    const err = (event as Event & { payload?: unknown }).payload;
    if (reloadIfStaleChunk(err)) {
      event.preventDefault();
    }
  });
}
